import { useEffect, useRef, useState } from 'react';
import { LuPlus } from 'react-icons/lu';

import { QuestionAddEndpoint } from '@core/types/types.endpoint.question';
import { Button } from '@frontend/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@frontend/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@frontend/components/ui/tooltip';
import { AnswerEditor } from '@frontend/shared/components/AnswerEditor';
import { CategorySelect } from '@frontend/shared/components/CategorySelect/CategorySelect';
import { DuplicateQuestionButton } from '@frontend/shared/components/DuplicateQuestionButton/DuplicateQuestionButton';
import { EditorButtons } from '@frontend/shared/components/Editor/Buttons/EditorButtons';
import { useSharedEditor } from '@frontend/shared/components/Editor/useSharedEditor';
import { TitleEditor } from '@frontend/shared/components/TitleEditor';
import { useBroadcaster } from '@frontend/shared/hooks/shared.hook.useBroadcaster';
import { useIsEditor } from '@frontend/shared/hooks/shared.hook.useIsEditor';
import { useDirectoryCategories } from '@frontend/shared/hooks/useDirectory';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';
import { useEditorState } from '@tiptap/react';
import { toast } from 'sonner';

export function AppTopbarAddQuestion() {
  const isEditor = useIsEditor();
  const fireEvent = useBroadcaster();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const categories = useDirectoryCategories();

  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState(categories?.[0]?.id || '');

  // Set default category when categories are loaded
  useEffect(() => {
    if (categories && categories.length > 0 && !categoryId) {
      setCategoryId(categories[0].id);
    }
  }, [categories, categoryId]);
  const { editor } = useSharedEditor({
    editable: true,
  });

  // Track editor content changes for button disabled state
  const { editorText } = useEditorState({
    editor,
    selector: (snapshot) => ({
      editorText: snapshot.editor.getText().trim(),
    }),
  });

  const adder = useSubmitter(async () => {
    const trimmed = title.trim();
    const newAnswerJson = JSON.stringify(editor?.getJSON());

    if (!editor || !trimmed || !categoryId) return;

    const [, res] = await callApi<QuestionAddEndpoint>('/question/add', {
      title: trimmed,
      answerBase64: btoa(encodeURIComponent(newAnswerJson)),
      category_id: categoryId,
    });

    if (res) {
      toast.success('Question added');
      setIsOpen(false);
      setTitle('');
      setCategoryId('');
      editor.commands.clearContent();
      fireEvent({ event: 'question:add', question: res });
    }
  });

  if (!isEditor) return null;

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              className="rounded-full bg-primary hover:bg-primary/90"
              onClick={() => setIsOpen(true)}
              aria-label="Add a new question"
            >
              <LuPlus className="h-5 w-5 text-white" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Add a new question</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl p-0">
          <DialogHeader className="px-6 py-4 bg-[rgba(3,52,38,0.03)] border-b border-[rgba(3,52,38,0.1)]">
            <DialogTitle className="text-base font-medium tracking-tight text-[#033426]">
              Add new question
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 py-4">
            <TitleEditor
              title={title}
              setTitle={setTitle}
              ref={inputRef}
              maxRows={10}
              className="pb-2 mb-4"
            />
            <AnswerEditor editor={editor} />
          </div>
          <DialogFooter className="gap-2 px-6 py-4 border-t">
            {editor && <EditorButtons editor={editor} />}
            <DuplicateQuestionButton title={title} inputRef={inputRef} />
            <CategorySelect
              value={categoryId}
              onChange={setCategoryId}
              isRequired
              label=""
              maxW="240px"
            />
            <Button
              disabled={!title.trim() || !editorText || !categoryId}
              onClick={adder.submit}
              className="bg-primary hover:bg-primary/90"
            >
              {adder.isSubmitting ? 'Saving...' : 'Save & verify'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
